<!--
  ServiceHealthPanel — semáforo por módulo + disponibilidad (T001-2).

  Es la misma lectura que ve el cliente en /portal, dentro del dashboard
  interno: el operador tiene que poder ver exactamente lo que su cliente
  ve, sin cambiar de sesión ni de URL.

  El veredicto llega resuelto del backend (umbrales por app en
  `semaphore_config`); acá no se recalcula nada. Un módulo en 'building'
  se muestra como "construyendo histórico", nunca como verde.
-->

<script setup>
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';

import { api } from '@/api/client.js';
import PanelCard from '@/components/panels/PanelCard.vue';
import { usePanelState } from '@/composables/usePanelState.js';
import { usePoll } from '@/composables/usePoll.js';
import { useTenantStore } from '@/stores/tenant.js';
import { SERVICE_HEALTH_EMPTY } from '@/utils/emptyStateCopy.js';

const STATE_LABEL = Object.freeze({
  green: 'Healthy',
  amber: 'Degraded',
  red: 'Impacted',
  building: 'Building history'
});

const tenant = useTenantStore();
const { activeAppId, timeWindow } = storeToRefs(tenant);
const ps = usePanelState('loading');

const semaphore = ref(null);
const uptime = ref(null);
const heartbeatHref = computed(() => `/probes/heartbeat?${new URLSearchParams({appId:activeAppId.value || ''}).toString()}`);

async function fetchFn({ signal }) {
  if (!activeAppId.value) return;
  const [sem, up] = await Promise.all([
    api.events.semaphore({ appId: activeAppId.value, window: timeWindow.value, signal }),
    api.events.uptime({ appId: activeAppId.value, window: timeWindow.value, signal })
  ]);
  if (!sem || sem.ok !== true) throw new Error('Invalid semaphore response');
  return { sem, up };
}

const { tick, status: refreshStatus, lastUpdatedAt } = usePoll(fetchFn, {
  watch: [() => tenant.changeKey],
  onSuccess(result) {
    semaphore.value = result.sem.semaphore ?? null;
    uptime.value = result.up?.uptime ?? null;
    ps.setState((semaphore.value?.modules?.length ?? 0) === 0 ? 'empty' : 'data');
  },
  onError(err) {
    ps.setState('error', {
      message: "Couldn't load service health",
      detail: err?.message ?? ''
    });
  }
});

// Catálogo y umbrales son por app: no arrastrar filas del tenant previo.
watch(() => tenant.changeKey, () => {
  semaphore.value = null;
  uptime.value = null;
  ps.setState('loading');
});

ps.setRetryHandler(() => tick());
defineExpose({ ps, refreshStatus });

const modules = computed(() => semaphore.value?.modules ?? []);
const appState = computed(() => semaphore.value?.app?.state ?? 'building');
const thresholds = computed(() => semaphore.value?.thresholds ?? null);
const counts = computed(() => {
  const acc = { green: 0, amber: 0, red: 0, building: 0 };
  for (const m of modules.value) acc[m.state] = (acc[m.state] ?? 0) + 1;
  return acc;
});

// Sin mediciones el ratio es null: se muestra guion, nunca 100 %.
function formatRatio(ratio) {
  if (ratio === null || ratio === undefined) return '—';
  return `${(ratio * 100).toFixed(2)}%`;
}

function label(state) {
  return STATE_LABEL[state] ?? STATE_LABEL.building;
}
</script>

<template>
  <PanelCard
    title="Service health"
    help-key="serviceHealth"
    :state="ps.state.value"
    :empty-headline="SERVICE_HEALTH_EMPTY.headline"
    :empty-body="SERVICE_HEALTH_EMPTY.body"
    :error-message="ps.errorMessage.value"
    :error-detail="ps.errorDetail.value"
    :on-retry="ps.retry"
    :refresh-status="refreshStatus"
    :last-updated-at="lastUpdatedAt"
  >
    <div class="health">
      <div class="health__summary">
        <div
          class="health__overall"
          :data-state="appState"
          data-test="health-overall"
        >
          <span
            class="health__dot health__dot--lg"
            :data-state="appState"
            aria-hidden="true"
          />
          <div>
            <strong>{{ label(appState) }}</strong>
            <span class="health__overall-sub">overall</span>
          </div>
        </div>

        <dl class="health__metrics">
          <div>
            <dt>Availability</dt>
            <dd data-test="health-uptime">
              {{ formatRatio(uptime?.uptimeRatio ?? null) }}
            </dd>
            <p class="health__hint">
              <template v-if="(uptime?.checks ?? 0) === 0">
                No probe checks in this window yet.
              </template>
              <template v-else>
                {{ uptime.checks }} checks, {{ uptime.downChecks }} counted as downtime.
              </template>
            </p>
          </div>
          <div>
            <dt>Modules</dt>
            <dd>
              {{ counts.green }} healthy · {{ counts.amber }} degraded ·
              {{ counts.red }} impacted · {{ counts.building }} building
            </dd>
            <p
              v-if="thresholds"
              class="health__hint"
            >
              Thresholds: amber at {{ thresholds.frictionAmber }}, red at
              {{ thresholds.frictionRed }} friction events per 100 accesses;
              {{ thresholds.minAccesses }} accesses minimum for a verdict.
            </p>
          </div>
        </dl>
      </div>

      <ul
        v-if="uptime?.targets?.length"
        class="health__targets"
      >
        <li
          v-for="t in uptime.targets"
          :key="t.targetId"
          data-test="health-target"
        >
          <span
            class="health__dot"
            :data-state="t.state === 'up' ? 'green' : (t.state === 'down' ? 'red' : 'building')"
            aria-hidden="true"
          />
          {{ t.name }} <span class="health__hint">{{ formatRatio(t.uptimeRatio) }}</span>
        </li>
      </ul>
      <p class="health__hint">
        Distributed Heartbeat observations are tracked separately until each target's ownership is transferred.
        <a :href="heartbeatHref">View targets and observers</a>
      </p>

      <ul class="health__modules">
        <li
          v-for="m in modules"
          :key="m.moduleKey"
          class="health__module"
          :data-state="m.state"
          data-test="health-module"
        >
          <span
            class="health__dot"
            :data-state="m.state"
            aria-hidden="true"
          />
          <span class="health__module-name">{{ m.name }}</span>
          <span class="health__module-state">{{ label(m.state) }}</span>
          <span class="health__hint">
            <template v-if="m.state === 'building'">
              {{ m.pageviews }} of {{ m.minAccesses }} accesses
            </template>
            <template v-else>
              {{ m.frictionRatio }} per 100 accesses ({{ m.frictionEvents }} of {{ m.pageviews }})
            </template>
          </span>
        </li>
      </ul>
    </div>

    <template #tableFallback>
      <table class="health__table">
        <caption class="visually-hidden">
          Semaphore state per module
        </caption>
        <thead>
          <tr>
            <th scope="col">
              Module
            </th>
            <th scope="col">
              State
            </th>
            <th
              scope="col"
              class="numeric"
            >
              Friction per 100
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="m in modules"
            :key="`table-${m.moduleKey}`"
          >
            <td>{{ m.name }}</td>
            <td>{{ label(m.state) }}</td>
            <td class="numeric">
              {{ m.frictionRatio ?? '—' }}
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </PanelCard>
</template>

<style scoped>
.health {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.health__summary {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-6);
  align-items: flex-start;
  padding: var(--space-4);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-xl);
  background: var(--bg-subtle);
}

.health__overall {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.health__overall strong {
  display: block;
  font-size: var(--text-xl);
  color: var(--fg-primary);
}

.health__overall-sub {
  color: var(--fg-tertiary);
  font-size: var(--text-xs);
}

.health__metrics {
  display: flex;
  gap: var(--space-6);
  margin: 0;
  flex-wrap: wrap;
}

.health__metrics dt {
  color: var(--fg-secondary);
  font-size: var(--text-sm);
}

.health__metrics dd {
  margin: var(--space-1) 0 0;
  color: var(--fg-primary);
  font-size: var(--text-lg);
  font-feature-settings: "tnum";
}

.health__hint {
  color: var(--fg-tertiary);
  font-size: var(--text-xs);
  margin: var(--space-1) 0 0;
}

.health__dot {
  width: .625rem;
  height: .625rem;
  border-radius: var(--radius-full);
  display: inline-block;
  background: var(--fg-tertiary);
  flex: none;
}

.health__dot--lg {
  width: 1rem;
  height: 1rem;
}

.health__dot[data-state="green"] {
  background: var(--success-icon);
}

.health__dot[data-state="amber"] {
  background: var(--warning-icon, var(--warning-fg));
}

.health__dot[data-state="red"] {
  background: var(--danger-icon, var(--danger-fg));
}

.health__modules, .health__targets {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: var(--space-2);
}

.health__targets li {
  display: flex;
  align-items: baseline;
  gap: var(--space-3);
  font-size: var(--text-sm);
}

.health__modules {
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 14rem), 1fr));
  gap: var(--space-3);
}

.health__module {
  --module-accent: var(--fg-tertiary);

  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-content: start;
  align-items: baseline;
  gap: var(--space-2);
  padding: var(--space-4);
  border: var(--bw-thin) solid var(--border-default);
  border-left: var(--space-1) solid var(--module-accent);
  border-radius: var(--radius-lg);
  background: var(--bg-canvas);
  font-size: var(--text-sm);
  overflow-wrap: anywhere;
}

.health__module[data-state="green"] {
  --module-accent: var(--success-icon);
}

.health__module[data-state="amber"] {
  --module-accent: var(--warning-icon, var(--warning-fg));
}

.health__module[data-state="red"] {
  --module-accent: var(--danger-icon, var(--danger-fg));
}

.health__module-name {
  color: var(--fg-primary);
  font-weight: var(--fw-semibold);
}

.health__module-state {
  grid-column: 2;
  color: var(--fg-secondary);
  font-size: var(--text-xs);
}

.health__module .health__hint {
  grid-column: 2;
  margin: 0;
}

.health__table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-xs);
}

.health__table th, .health__table td {
  padding: var(--space-1) var(--space-2);
  text-align: left;
  border-bottom: var(--bw-thin) solid var(--border-default);
}

.health__table .numeric {
  text-align: right;
  font-feature-settings: "tnum";
}
</style>
